import {createStore} from 'vuex';
import TokenService from '@services/tokenService';
import route from '@router';
import {jwtDecode} from 'jwt-decode';
import roleService from '@services/roleService';
import {Crisp} from 'crisp-sdk-web';
import {useCompanyStore} from '@store/companyStore';
import * as amplitude from '@amplitude/analytics-browser';
import {initAmplitude} from '../utils';
import socketService from '@services/socketService';

export const packageVersion = require('@/../package.json').version;

export default createStore({
  state: {
    currentUser: getSavedState('auth.currentUser'),
    userInformation: getSavedState('userInformation'),
    nbActivityAdded: 0,
    nbTicketStatusChanged: 0,
    refreshPromise: null,
    packageVersion: packageVersion || '0',
    title: undefined,
  },
  mutations: {
    SET_CURRENT_USER(state, newValue) {
      state.currentUser = newValue;
      saveState('auth.currentUser', newValue);
    },
    SET_USER_INFO(state, newValue) {
      if (newValue && newValue.jwt) {
        const decoded = jwtDecode(newValue.jwt);
        const value = {
          userId: decoded.userId,
          firstName: decoded.firstName,
          lastName: decoded.lastName,
          email: decoded.login,
          roleKey: decoded.roleKey,
          roleType: decoded.roleType,
          companyId: decoded.companyId,
          employeeId: decoded.employeeId,
          partenaireId: decoded.partenaireId,
          housekeeperId: decoded.housekeeperId,
          resetPassword: decoded.resetPassword,
          poleTechnicalKeys: decoded.poleTechnicalKeys ?? [],
          ownerId: decoded.ownerId,
          showDashboard: decoded.showDashboard,
          commercialId: decoded.commercialId,
          crispPhone: decoded.crispPhone,
          crispNickname: decoded.crispNickname,
          crispMail: decoded.crispMail,
          accommodationIds: decoded.accommodationIds,
          twoFactorEnabled: decoded.twoFactorEnabled ?? false,
          isFirstLogin: decoded.isFirstLogin,
          providerId: decoded.providerId,
        };
        state.userInformation = value;
        window.localStorage.setItem('refreshToken', newValue.refreshToken);
        saveState('userInformation', value);
      } else {
        removeState('userInformation');
      }
    },
    SET_USER_PERMISSIONS(state, newValue) {
      state.userInformation.permissions = newValue;
      saveState('userInformation', state.userInformation);
    },
    ERASE_RESET_PWD(state, newValue) {
      state.userInformation.resetPassword = newValue;
      saveState('userInformation', state.userInformation);
    },
    SET_TWO_FACTOR_STATUS(state, enabled) {
      if (state.userInformation) {
        state.userInformation.twoFactorEnabled = enabled;
        saveState('userInformation', state.userInformation);
      }
    },
    SET_FIRST_LOGIN(state, isFirstLogin) {
      if (state.userInformation) {
        state.userInformation.isFirstLogin = isFirstLogin;
        saveState('userInformation', state.userInformation);
      }
    },
    ERASE_USER_INFORMATION(state) {
      state.userInformation = undefined;
      removeState('userInformation');
    },
    SET_TITLE(state, newValue) {
      state.title = newValue;
    },
    ACTIVITY_ADDED(state) {
      state.nbActivityAdded++;
    },
    TICKET_STATUS_CHANGED(state) {
      state.nbTicketStatusChanged++;
    },
  },
  getters: {
    // Whether the user is currently logged in.
    loggedIn(state) {
      return !!state.currentUser;
    },
    username(state) {
      return state.userInformation.firstName;
    },
    userLogin(state) {
      return state.userInformation.email;
    },
    userId(state) {
      return state.userInformation.userId;
    },
    refreshPromise(state) {
      return state.refreshPromise;
    },
    appVersion: (state) => {
      return state.packageVersion;
    },
    title: (state) => {
      return state.title;
    },
  },
  actions: {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    init({dispatch}) {
      dispatch('validate');
    },
    setTitle({commit, dispatch, getters}, {title} = {}) {
      commit('SET_TITLE', title);
    },
    eraseResetPwd({commit}) {
      commit('ERASE_RESET_PWD', false);
    },
    eraseUserInformation({commit}) {
      commit('ERASE_USER_INFORMATION');
    },
    // Logs in the current user.
    logIn({commit, dispatch, state}, {login, password, providerId, otpCode, deviceId, rememberMe, otpPhone, sid} = {}) {
      dispatch('logOutSilent');
      var user = {login, password, providerId, otpCode, deviceId, rememberMe, otpPhone, sid};
      return TokenService.login(user).then((data) => {
        if (data.status === 'PENDING_2FA' || data.status === 'SETUP_2FA') return data;
        return _doSetUser({commit, state}, data);
      });
    },
    setUser({commit, state}, data) {
      return _doSetUser({commit, state}, data);
    },
    refresh({commit, dispatch, getters, state}) {
      if (null != state.refreshPromise) return state.refreshPromise;

      var refreshToken = {
        refreshToken: window.localStorage.getItem('refreshToken'),
        login: state.currentUser.login,
        providerId: state.currentUser.providerId,
      };
      state.refreshPromise = TokenService.refresh(refreshToken)
        .then((data) => {
          state.refreshPromise = null;
          const user = data;
          commit('SET_CURRENT_USER', user);
          commit('SET_USER_INFO', user);
          return roleService.permissions(this.state.userInformation.roleKey).then((res) => {
            commit('SET_USER_PERMISSIONS', res);
            return user;
          });
        })
        .finally(() => {
          state.refreshPromise = null;
        });

      return state.refreshPromise;
    },

    verifyTotp({commit, state}, {login, providerId, otpCode}) {
      const deviceId = window.localStorage.getItem('ga-device-id');
      return TokenService.verifyTotp({login, providerId, otpCode, deviceId}).then((data) =>
        _doSetUser({commit, state}, data),
      );
    },

    // Logs out the current user.
    logOut({commit}) {
      return TokenService.logout()
        .catch(() => {})
        .finally(() => {
          Crisp.setTokenId(); // 1. Clear the token value
          Crisp.session.reset(); // 2. Unbind the current session
          window.localStorage.removeItem('filters');
          useCompanyStore().clear();
          socketService.reset();
          commit('SET_CURRENT_USER', null);
          commit('SET_USER_INFO', null);
          amplitude.reset();
          route.push({name: 'login'});
        });
    },

    logOutSilent({commit}) {
      Crisp.setTokenId(); // 1. Clear the token value
      Crisp.session.reset(); // 2. Unbind the current session
      useCompanyStore().clear();
      window.localStorage.removeItem('filters');
      socketService.reset();
      commit('SET_CURRENT_USER', null);
      commit('SET_USER_INFO', null);
      amplitude.reset();
    },

    logOutExpired({commit}) {
      Crisp.setTokenId(); // 1. Clear the token value
      Crisp.session.reset(); // 2. Unbind the current session
      window.localStorage.removeItem('filters');
      useCompanyStore().clear();
      socketService.reset();
      commit('SET_CURRENT_USER', null);
      commit('SET_USER_INFO', null);
      route.push({name: 'login', query: {redirectFrom: route.currentRoute.value.fullPath}});
      amplitude.reset();
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    validate({state}) {
      if (!state.currentUser) return Promise.resolve(null);
      initAmplitude();
      return state.currentUser;
    },
  },
  modules: {},
});

// ===
// Private helpers
// ===

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key));
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function removeState(key) {
  window.localStorage.removeItem(key);
}

function _doSetUser({commit, state}, user) {
  if (user?.deviceId) window.localStorage.setItem('ga-device-id', user.deviceId);
  commit('SET_CURRENT_USER', user);
  commit('SET_USER_INFO', user);
  initAmplitude();
  return roleService.permissions(state.userInformation.roleKey).then((res) => {
    commit('SET_USER_PERMISSIONS', res);
    return user;
  });
}
